/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Callout, Tldr, FaqBlock, InlineCTA } from "./shared";

export const RankHigherChatgptGoogleBody = ({ post, affiliateUrl, onTrack }) => (
  <>
    <p className="lead text-lg leading-relaxed text-white/80">
      Your buyers no longer choose between Google and ChatGPT — they use both. They ask Google
      when they want options. They ask ChatGPT when they want an answer. To win in 2026 your
      content has to rank on both. The good news: the same article can do both jobs if you
      structure it right.
    </p>

    <Tldr>{post.tldr}</Tldr>

    <h2 id="why-both">Why ranking on both ChatGPT and Google matters</h2>
    <p>Three shifts are happening at once:</p>
    <ul>
      <li><strong>Google AI Overviews</strong> now sit above classic blue links on most informational queries — and they're built from the same web pages, but only quote a small set of them.</li>
      <li><strong>ChatGPT Search</strong> (live since late 2024) pulls real-time web results through Bing + curated partners. It cites 3–5 sources per answer.</li>
      <li><strong>Click-through is shrinking</strong> on classic results, but <strong>brand recall is exploding</strong> for sites cited inside AI answers.</li>
    </ul>
    <p>
      Translation: if you only optimize for Google's blue links, you get fewer clicks. If you
      only optimize for ChatGPT, you miss the 4 billion daily Google searches. You need a
      single piece of content that does both jobs.
    </p>

    <h2 id="how-each-ranks">How Google and ChatGPT actually rank content</h2>
    <div className="my-6 overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-white/45">
          <tr>
            <th className="p-3">Signal</th>
            <th className="p-3">Google (classic + AI Overviews)</th>
            <th className="p-3">ChatGPT Search</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {[
            ["Primary index", "Google's own index", "Bing index + curated partners"],
            ["Ranking signal", "Backlinks + on-page + UX", "Entity authority + extractability"],
            ["Quote unit", "Featured snippet (40–60 words)", "Cited paragraph or list"],
            ["Freshness weight", "High on news, medium on evergreen", "Very high on time-sensitive queries"],
            ["Schema usage", "Rich results (FAQ, HowTo, Article)", "Reads same schema to extract answers"],
            ["Brand mentions", "Indirect (via PR)", "Direct ranking signal"],
            ["Click reward", "Yes — sends a click", "Sometimes — names you in answer"],
          ].map((r) => (
            <tr key={r[0]}>
              {r.map((c, i) => (
                <td key={i} className={`p-3 ${i === 0 ? "font-display font-bold text-white" : "text-white/80"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <Callout kind="info">
      <strong>The big insight:</strong> Google and ChatGPT use <em>different</em> indexes but
      the <em>same</em> page-level signals — schema, headings, extractable answers, fresh
      dates, brand entity. That's why one well-built page can rank in both.
    </Callout>

    <h2 id="playbook">The 8-step dual-platform playbook</h2>

    <h3>Step 1 — One topic per URL (and stop splitting authority)</h3>
    <p>
      The #1 reason brands rank on neither platform is they publish 3 thin articles on the
      same topic. Consolidate. Pick one canonical URL per topic, redirect or merge the rest,
      and make that one page the definitive answer. Google rewards depth. ChatGPT prefers the
      page with the highest entity score on the topic.
    </p>

    <h3>Step 2 — Open every page with a 50-word answer block</h3>
    <p>
      The first 2–3 sentences after the H1 (and after every question-style H2) should be a
      tight, self-contained answer. Google lifts that for featured snippets. ChatGPT lifts it
      for citations. Same block, two wins.
    </p>
    <Callout kind="do">
      <strong>Do:</strong> "How do you rank higher on ChatGPT and Google? Publish one
      definitive URL per topic, lead with a 50-word answer block, mark it up with FAQ + HowTo
      schema, and earn brand mentions alongside backlinks."
    </Callout>

    <h3>Step 3 — Mark every page with the right schema</h3>
    <p>For dual ranking, three JSON-LD blocks earn their keep on almost any page:</p>
    <ul>
      <li><code>Article</code> or <code>BlogPosting</code> — tells both engines what the page is</li>
      <li><code>FAQPage</code> — Google shows it as rich results; ChatGPT extracts answers</li>
      <li><code>HowTo</code> — only when you have genuine ordered steps; both engines quote it</li>
    </ul>
    <p>
      Validate every page with{" "}
      <a href="https://search.google.com/test/rich-results" target="_blank" rel="noopener noreferrer">
        Google's Rich Results Test
      </a>.
    </p>

    <h3>Step 4 — Build E-E-A-T + entity authority</h3>
    <p>
      Google rewards <strong>E-E-A-T</strong> (Experience, Expertise, Authoritativeness,
      Trustworthiness). ChatGPT rewards a clean <strong>entity graph</strong>. Same idea, two
      labels. Cover both by:
    </p>
    <ul>
      <li>Adding <code>Organization</code> JSON-LD to your site root with logo, founder, sameAs links</li>
      <li>Adding a <code>Person</code> schema for the author of every post with credentials</li>
      <li>Including author bio + headshot at the bottom of every article</li>
      <li>Linking to LinkedIn, Wikipedia, Crunchbase, podcast appearances</li>
    </ul>

    <h3>Step 5 — Cover both intents on the same URL</h3>
    <p>
      Most pages serve only one intent (informational <em>or</em> commercial). To rank on both
      platforms, the page should answer the question <em>and</em> tee up the buying action.
      Structure:
    </p>
    <ol>
      <li><strong>Top:</strong> answer the question crisply (wins ChatGPT citation)</li>
      <li><strong>Middle:</strong> depth, examples, schemas (wins Google ranking)</li>
      <li><strong>Bottom:</strong> next step / product recommendation (wins conversion)</li>
    </ol>

    <h3>Step 6 — Build internal topic clusters</h3>
    <p>
      Both engines reward sites that demonstrate <strong>topical authority</strong>. Pick 1
      pillar page, write 5–8 supporting posts, and cross-link them in both directions. We do
      this on our own blog — see{" "}
      <a href="/blog/how-to-rank-on-ai-search-engines">how to rank on AI search engines</a>,{" "}
      <a href="/blog/what-is-aeo-answer-engine-optimization">what is AEO</a> and{" "}
      <a href="/blog/geo-vs-seo">GEO vs SEO</a>.
    </p>

    <h3>Step 7 — Earn citations on both sides</h3>
    <p>
      Google still needs <strong>backlinks</strong> from authoritative domains. ChatGPT
      weights <strong>unlinked brand mentions</strong> (Reddit, Substack, podcasts, YouTube
      transcripts) just as heavily. Run outreach for both:
    </p>
    <ul>
      <li>For Google: guest posts, HARO/Help A B2B Writer, digital PR</li>
      <li>For ChatGPT: Reddit AMAs, podcast appearances, niche newsletters, "best of" lists</li>
    </ul>

    <h3>Step 8 — Refresh on a 90-day cadence</h3>
    <p>
      Both platforms have a strong recency bias. Bump the publish date, add a "Updated [month]
      2026" line, refresh stats, and re-submit the URL via Google Search Console + Bing
      Webmaster Tools.
    </p>

    <InlineCTA
      post={post}
      location="midpost"
      affiliateUrl={affiliateUrl}
      onTrack={onTrack}
      label={{
        title: "Want every page to rank on Google AND ChatGPT — on autopilot?",
        sub: "Soro researches, writes, schema-injects and publishes daily content that wins both platforms.",
        cta: "Try Soro free",
      }}
    />

    <h2 id="mistakes">5 mistakes that kill dual ranking</h2>
    <ol>
      <li><strong>Hiding the answer.</strong> 600 words of intro means neither Google nor ChatGPT can extract the answer.</li>
      <li><strong>Blocking GPTBot.</strong> Default Yoast/Rank Math settings sometimes disallow it. Audit your robots.txt.</li>
      <li><strong>Faking schema.</strong> Adding FAQPage JSON-LD without visible FAQ content = Google penalty.</li>
      <li><strong>Publishing duplicate articles.</strong> Splits authority across both engines.</li>
      <li><strong>Ignoring brand mentions.</strong> ChatGPT cites brands it has seen named, not just linked.</li>
    </ol>

    <h2 id="measurement">How to know it's working</h2>
    <p>Track these monthly across both platforms:</p>
    <ul>
      <li><strong>Google:</strong> Search Console impressions, average position, AI Overview appearance</li>
      <li><strong>ChatGPT:</strong> manually prompt 10 category questions monthly — count citations of your brand</li>
      <li><strong>Brand search:</strong> Google "your brand name" volume in GSC — a leading indicator of GEO compounding</li>
    </ul>
    <p>You should see Google impressions climb in months 1–3 and ChatGPT citations appear in months 2–4 if the playbook is executed cleanly.</p>

    <FaqBlock faq={post.faq} />

    <h2 id="next-steps">Your next 7 days</h2>
    <ol>
      <li>Audit your robots.txt — allow GPTBot, Google-Extended, PerplexityBot, Anthropic-AI, CCBot</li>
      <li>Pick your top 5 ranking pages, add FAQPage schema to each</li>
      <li>Add Organization + Person JSON-LD to your site root</li>
      <li>Identify your 3 most thin/duplicate articles and consolidate them</li>
      <li>Set a 90-day refresh calendar in Notion or Asana</li>
    </ol>

    <p>Do that and you'll see movement on both platforms within 30 days.</p>

    <p>Want this on autopilot? <a href={affiliateUrl} target="_blank" rel="sponsored noopener noreferrer" onClick={() => onTrack("inline-mention", post.slug)}>Try Soro</a> — it ships every article ready for both Google and ChatGPT, daily.</p>
  </>
);
